import { z } from "zod";

import {
  getGeneratedImageDataUrl,
  getGeneratedImageUrl,
} from "@/lib/generatedRecommendationAssets";
import { isValidHttpUrlWithHostname, normalizePublicWebsiteUrl } from "@/lib/websiteUrl";

export const CARPET_LITE_ORGANIC_EXPERIENCE_GATE = "bazaar.tapestry.organic_campaign_preview";
export const CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY =
  "adsManagerCarpetLiteCampaignContinuation";
export const CARPET_LITE_LAUNCH_CONFIRMATION_QUERY_PARAM = "carpet_lite_launch";
export const CARPET_LITE_AD_TITLE_MAX_LENGTH = 50;
export const CARPET_LITE_AD_BODY_MAX_LENGTH = 52;
export const CARPET_LITE_TARGET_URL_MAX_LENGTH = 2048;

export class CarpetLiteWebsiteCrawlingDisallowedError extends Error {
  constructor() {
    super("This website does not allow us to read its landing page.");
    this.name = "CarpetLiteWebsiteCrawlingDisallowedError";
  }
}

export class CarpetLiteWebsiteCrawlingUnavailableError extends Error {
  constructor() {
    super("We couldn't access this website to create an ad.");
    this.name = "CarpetLiteWebsiteCrawlingUnavailableError";
  }
}

const carpetLiteCampaignSettingsSchema = z.object({
  objective: z.enum(["clicks", "reach"]),
  locationCountryCodes: z.array(z.string().regex(/^[A-Z]{2}$/)).min(1),
  budgetType: z.enum(["daily", "lifetime"]).default("daily"),
  budgetAmount: z.string().regex(/^\d+(?:\.\d{1,2})?$/),
  bidMode: z.enum(["automated", "manual"]),
  maxBidAmount: z
    .string()
    .max(16)
    .regex(/^\d+(?:\.\d{1,2})?$/)
    .refine((value) => Number(value) > 0)
    .optional(),
});

const carpetLiteCreativeOverrideSchema = z.object({
  title: z.string().trim().min(1).max(CARPET_LITE_AD_TITLE_MAX_LENGTH),
  body: z.string().trim().min(1).max(CARPET_LITE_AD_BODY_MAX_LENGTH),
  targetUrl: z
    .string()
    .max(CARPET_LITE_TARGET_URL_MAX_LENGTH)
    .refine(isValidHttpUrlWithHostname)
    .optional(),
});

const carpetLiteAccountSetupSchema = z.object({
  businessName: z.string().trim().min(3).max(120),
  businessWebsite: z.string().refine((value) => {
    try {
      return normalizePublicWebsiteUrl(value) === value;
    } catch {
      return false;
    }
  }),
  logoFileId: z.string().trim().min(1).max(255).optional(),
  logoRemoved: z.literal(true).optional(),
});

const carpetLiteCampaignContinuationSchema = z.object({
  version: z.literal(1),
  draftId: z.string().regex(/^[a-f0-9]{64}$/),
  validUntil: z.string().datetime({ offset: true }),
  settings: carpetLiteCampaignSettingsSchema,
  creativeOverride: carpetLiteCreativeOverrideSchema.optional(),
  accountSetup: carpetLiteAccountSetupSchema.optional(),
  logoCandidateUrl: z.string().max(2_048).optional(),
});

export type CarpetLiteCampaignSettings = z.infer<typeof carpetLiteCampaignSettingsSchema>;
export type CarpetLiteCreativeOverride = z.infer<typeof carpetLiteCreativeOverrideSchema>;
export type CarpetLiteCampaignContinuation = z.infer<typeof carpetLiteCampaignContinuationSchema>;

type CarpetLiteCampaignStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const generatedAdSchema = z.object({
  name: z.string(),
  creative: z.object({
    title: z.string(),
    body: z.string(),
    target_url: z.string(),
  }),
  generated_asset: z.object({
    image_url: z.string().nullable(),
    image_base64: z.string().nullable(),
    file_id: z.string().nullable(),
  }),
});

const carpetLiteCampaignResponseSchema = z.object({
  generation_id: z.string().uuid(),
  draft_id: z.string().regex(/^[a-f0-9]{64}$/),
  valid_until: z.string().datetime({ offset: true }),
  cache_hit: z.boolean(),
  crawl_status: z.enum(["available", "unavailable"]).optional(),
  logo_candidate_url: z.string().nullable().optional(),
  campaign: z.object({
    name: z.string(),
    landing_page_url: z.string().nullable(),
    ad_groups: z.array(
      z.object({
        name: z.string(),
        context_hints: z.array(z.string()),
        ads: z.array(generatedAdSchema).min(1),
      }),
    ),
  }),
});

export type CarpetLiteCampaignPreview = z.infer<typeof carpetLiteCampaignResponseSchema>;

export function buildCarpetLiteDestinationUrl(baseUrl: string, rawSuffix: string): string | null {
  if (
    rawSuffix.length > 0 &&
    (rawSuffix !== rawSuffix.trim() ||
      /\s/.test(rawSuffix) ||
      rawSuffix.includes("#") ||
      (!rawSuffix.startsWith("/") && !rawSuffix.startsWith("?")) ||
      rawSuffix.startsWith("//"))
  ) {
    return null;
  }

  try {
    const targetUrl = new URL(baseUrl);
    const normalizedBaseUrl = targetUrl.toString();
    if (
      !isValidHttpUrlWithHostname(normalizedBaseUrl) ||
      targetUrl.username ||
      targetUrl.password
    ) {
      return null;
    }

    if (rawSuffix.length === 0) {
      return normalizedBaseUrl.length <= CARPET_LITE_TARGET_URL_MAX_LENGTH ?
          normalizedBaseUrl
        : null;
    }

    if (rawSuffix.startsWith("?")) {
      const querySuffix = rawSuffix.slice(1);
      if (querySuffix.length === 0) {
        return null;
      }
      targetUrl.search = targetUrl.search ? `${targetUrl.search}&${querySuffix}` : rawSuffix;
    } else {
      const suffixUrl = new URL(rawSuffix, targetUrl.origin);
      if (suffixUrl.hash) {
        return null;
      }
      const basePathname =
        targetUrl.pathname.endsWith("/") && targetUrl.pathname !== "/" ?
          targetUrl.pathname.slice(0, -1)
        : targetUrl.pathname === "/" ? ""
        : targetUrl.pathname;
      targetUrl.pathname = `${basePathname}${suffixUrl.pathname}`;
      if (suffixUrl.search) {
        targetUrl.search =
          targetUrl.search ? `${targetUrl.search}&${suffixUrl.search.slice(1)}` : suffixUrl.search;
      }
    }

    const appendedUrl = targetUrl.toString();
    return (
        appendedUrl.length <= CARPET_LITE_TARGET_URL_MAX_LENGTH &&
          isAllowedCarpetLiteDestinationOverride(normalizedBaseUrl, appendedUrl)
      ) ?
        appendedUrl
      : null;
  } catch {
    return null;
  }
}

export async function generateCarpetLiteCampaign({
  apiUrl,
  websiteUrl,
  accessToken,
  generationId = crypto.randomUUID(),
}: {
  apiUrl: string;
  websiteUrl: string;
  accessToken?: string;
  generationId?: string;
}): Promise<CarpetLiteCampaignPreview> {
  return requestCarpetLiteCampaign({ apiUrl, websiteUrl, accessToken, generationId });
}

export async function regenerateCarpetLiteCampaignImage({
  apiUrl,
  websiteUrl,
  draftId,
  accessToken,
  generationId = crypto.randomUUID(),
}: {
  apiUrl: string;
  websiteUrl: string;
  draftId: string;
  accessToken?: string;
  generationId?: string;
}): Promise<CarpetLiteCampaignPreview> {
  return requestCarpetLiteCampaign({
    apiUrl,
    websiteUrl,
    accessToken,
    generationId,
    regenerateImageDraftId: draftId,
  });
}

async function requestCarpetLiteCampaign({
  apiUrl,
  websiteUrl,
  accessToken,
  generationId,
  regenerateImageDraftId,
}: {
  apiUrl: string;
  websiteUrl: string;
  accessToken?: string;
  generationId: string;
  regenerateImageDraftId?: string;
}): Promise<CarpetLiteCampaignPreview> {
  const normalizedWebsiteUrl = normalizePublicWebsiteUrl(websiteUrl);
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessToken != null) {
    headers.authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(new URL("/v1/ui/recommendations/campaigns/generate-fast", apiUrl), {
    method: "POST",
    headers,
    body: JSON.stringify({
      entry_point: "campaigns_null_state",
      url: normalizedWebsiteUrl,
      generation_id: generationId,
      ...(regenerateImageDraftId == null ?
        {}
      : { regenerate_image_draft_id: regenerateImageDraftId }),
    }),
  });
  if (!response.ok) {
    const errorBody: unknown = await response.json().catch(() => null);
    const errorCode = getSofaErrorCode(errorBody);
    if (errorCode === "website_crawling_disallowed") {
      throw new CarpetLiteWebsiteCrawlingDisallowedError();
    }
    if (errorCode === "website_crawling_unavailable") {
      throw new CarpetLiteWebsiteCrawlingUnavailableError();
    }
    throw new Error("We couldn't generate a campaign for that website. Please try again.");
  }
  const campaign = carpetLiteCampaignResponseSchema.parse(await response.json());
  if (campaign.crawl_status !== "available") {
    throw new CarpetLiteWebsiteCrawlingUnavailableError();
  }
  return campaign;
}

function getSofaErrorCode(value: unknown): unknown {
  if (typeof value !== "object" || value == null || !("error" in value)) {
    return null;
  }
  const error = value.error;
  return typeof error === "object" && error != null && "code" in error ? error.code : null;
}

export function getCarpetLiteLogoCandidateUrl(
  preview: CarpetLiteCampaignPreview | null | undefined,
): string | null {
  return getGeneratedImageUrl(preview?.logo_candidate_url) ?? null;
}

export function getCarpetLiteAdImageUrl(
  generatedAd: CarpetLiteCampaignPreview["campaign"]["ad_groups"][number]["ads"][number],
): string | null {
  const asset = generatedAd.generated_asset;
  return (
    getGeneratedImageUrl(asset.image_url) ?? getGeneratedImageDataUrl(asset.image_base64) ?? null
  );
}

export function storeCarpetLiteCampaignContinuation(
  preview: CarpetLiteCampaignPreview,
  settings: CarpetLiteCampaignSettings,
  creativeOverride?: CarpetLiteCreativeOverride,
  {
    businessName,
    fallbackWebsiteUrl,
    logoFileId,
    logoRemoved,
    storage = getSessionStorage(),
  }: {
    businessName?: string;
    fallbackWebsiteUrl?: string;
    logoFileId?: string;
    logoRemoved?: boolean;
    storage?: CarpetLiteCampaignStorage | null;
  } = {},
): boolean {
  if (!storage) {
    return false;
  }
  const generatedDestinationUrl = getCarpetLiteGeneratedDestinationUrl(preview);
  if (
    creativeOverride?.targetUrl != null &&
    (generatedDestinationUrl == null ||
      !isAllowedCarpetLiteDestinationOverride(generatedDestinationUrl, creativeOverride.targetUrl))
  ) {
    return false;
  }
  const continuation = carpetLiteCampaignContinuationSchema.safeParse({
    version: 1,
    draftId: preview.draft_id,
    validUntil: preview.valid_until,
    settings,
    creativeOverride,
    accountSetup: getCarpetLiteAccountSetup(preview, {
      businessName,
      fallbackWebsiteUrl,
      logoFileId,
      logoRemoved,
    }),
    logoCandidateUrl: getCarpetLiteLogoCandidateUrl(preview) ?? undefined,
  });
  if (!continuation.success || Date.parse(continuation.data.validUntil) <= Date.now()) {
    return false;
  }
  try {
    storage.setItem(
      CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY,
      JSON.stringify(continuation.data),
    );
    return true;
  } catch {
    return false;
  }
}

function getCarpetLiteAccountSetup(
  preview: CarpetLiteCampaignPreview,
  {
    businessName,
    fallbackWebsiteUrl,
    logoFileId,
    logoRemoved,
  }: {
    businessName?: string;
    fallbackWebsiteUrl?: string;
    logoFileId?: string;
    logoRemoved?: boolean;
  },
): z.infer<typeof carpetLiteAccountSetupSchema> | undefined {
  const rawBusinessWebsite = preview.campaign.landing_page_url ?? fallbackWebsiteUrl;
  if (rawBusinessWebsite == null) {
    return undefined;
  }
  try {
    const businessWebsite = normalizePublicWebsiteUrl(rawBusinessWebsite);
    const normalizedBusinessName =
      businessName?.trim() || new URL(businessWebsite).hostname.replace(/^www\./, "");
    return {
      businessName: normalizedBusinessName.slice(0, 120),
      businessWebsite,
      ...(logoFileId ? { logoFileId } : {}),
      ...(logoRemoved ? { logoRemoved: true as const } : {}),
    };
  } catch {
    return undefined;
  }
}

function getCarpetLiteGeneratedDestinationUrl(preview: CarpetLiteCampaignPreview): string | null {
  return (
    preview.campaign.ad_groups[0]?.ads[0]?.creative.target_url ??
    preview.campaign.landing_page_url ??
    null
  );
}

export function isAllowedCarpetLiteDestinationOverride(
  baseUrl: string,
  targetUrl: string,
): boolean {
  try {
    const base = new URL(baseUrl);
    const target = new URL(targetUrl);
    if (
      !isValidHttpUrlWithHostname(base.toString()) ||
      !isValidHttpUrlWithHostname(target.toString()) ||
      base.username ||
      base.password ||
      target.username ||
      target.password ||
      base.origin !== target.origin ||
      base.hash !== target.hash
    ) {
      return false;
    }

    const basePathname =
      base.pathname.endsWith("/") && base.pathname !== "/" ?
        base.pathname.slice(0, -1)
      : base.pathname;
    const hasAllowedPath =
      basePathname === "/" ?
        target.pathname.startsWith("/")
      : target.pathname === basePathname || target.pathname.startsWith(`${basePathname}/`);
    if (!hasAllowedPath) {
      return false;
    }

    const baseQuery = base.search.slice(1);
    const targetQuery = target.search.slice(1);
    return (
      baseQuery.length === 0 || targetQuery === baseQuery || targetQuery.startsWith(`${baseQuery}&`)
    );
  } catch {
    return false;
  }
}

export function getStoredCarpetLiteCampaignContinuation(
  storage: CarpetLiteCampaignStorage | null = getSessionStorage(),
  now = Date.now(),
): CarpetLiteCampaignContinuation | null {
  if (!storage) {
    return null;
  }
  try {
    const rawContinuation = storage.getItem(CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY);
    const continuation = carpetLiteCampaignContinuationSchema.safeParse(
      rawContinuation == null ? null : JSON.parse(rawContinuation),
    );
    if (!continuation.success || Date.parse(continuation.data.validUntil) <= now) {
      storage.removeItem(CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY);
      return null;
    }
    return continuation.data;
  } catch {
    try {
      storage.removeItem(CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY);
    } catch {
      // Storage is optional; onboarding remains usable when it is unavailable.
    }
    return null;
  }
}

export function clearStoredCarpetLiteCampaignContinuation(
  storage: CarpetLiteCampaignStorage | null = getSessionStorage(),
): void {
  try {
    storage?.removeItem(CARPET_LITE_CAMPAIGN_CONTINUATION_STORAGE_KEY);
  } catch {
    // Storage is optional; onboarding remains usable when it is unavailable.
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
